import React, { useEffect, useRef, useState } from 'react';
import { take } from '../../utils/pendingRoomDocs';
import { chatGameApi } from './chatGameApi';

// 📎 10-02 room docs (hidden): the room half of the build form's "Docs for the
// AIs" (utils/pendingRoomDocs). GameStepPage mounts it once at the page root.
// Once the page holds a seat (`seat`) whose secret this browser stores, it
// takes THIS room's kept files, once, and sends each through the page's own
// upload (`upload` = handleUploadFile, the 📎's door) with { knowledge: true },
// one at a time. They land in the chat and in Files like any upload. Nothing
// kept for the room (every room but a just-built one) = nothing happens: no
// call, no markup.
//
// A failed upload shows its reason the way FilesFolder's "Add a file" does (the
// error's own message, else "Couldn’t add that file."), here as a note above
// the message box with Try again (the same files, still kept) and ✕. 📎 review
// F7: the upload function is checked per file, and an engine error (a TypeError
// such as "x is not a function" or a failed fetch, a SyntaxError from a bad
// body…) never shows its raw text: the plain line does. The note never repeats
// "Try again" (its button says it).
//
// Props:
//   gameId     the room id (the URL's gameId, the key BuildSpaceV2 kept them under)
//   seat       the page's seat id (humanPlayerId), or null until it has one
//   upload     async (file, opts) => void; throws an Error whose message is shown
//   secretFor  optional (seatId) => secret; default chatGameApi.getPlayerSecret
//
// Class names avoid every substring generated themes target (card · panel ·
// chip · message · btn · button · badge · tag · user · player · header ·
// popover · tooltip); the buttons carry data-prop-ui (ThemeStyleInjector's
// background shield) and their own armor, like FilesFolder's.

export const ROOM_DOCS_FAILED = 'Couldn’t add that file.'; // FilesFolder's ADD_FAILED, minus its "Try again." (the button says it)

const CSS = `
.rdu-note, .rdu-note * { box-sizing: border-box; }
.rdu-note { position: fixed; left: 50%; transform: translateX(-50%); bottom: calc(96px + env(safe-area-inset-bottom, 0px)); z-index: 100012;
  width: max-content; max-width: min(440px, calc(100vw - 32px)); display: flex; align-items: flex-start; gap: 10px; padding: 10px 10px 10px 14px;
  border-radius: 12px; background: #1B1F27; color: #ECEEF2; box-shadow: 0 0 0 1px #343B48, 0 14px 32px rgba(0,0,0,.45);
  font: 400 13px/1.4 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; text-align: left; text-shadow: none; }
.rdu-list { flex: 1; min-width: 0; margin: 0; padding: 0; list-style: none; }
.rdu-note .rdu-list > li { margin: 0 !important; padding: 0 !important; list-style: none !important; overflow-wrap: anywhere; }
.rdu-list b { font-weight: 600; }
.rdu-note .rdu-go.rdu-go, .rdu-note .rdu-x.rdu-x { box-sizing: border-box !important; margin: 0 !important; text-shadow: none !important; transform: none !important;
  min-width: 0 !important; min-height: 0 !important; max-width: none !important; text-transform: none !important; letter-spacing: normal !important; cursor: pointer;
  border: 0 !important; color: #ECEEF2 !important; flex: none; }
.rdu-note .rdu-go.rdu-go { width: auto !important; height: 28px !important; padding: 0 10px !important; border-radius: 999px !important;
  box-shadow: inset 0 0 0 1.5px #ECEEF2 !important; font: 600 12.5px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important; white-space: nowrap; }
.rdu-note .rdu-go.rdu-go:disabled { opacity: .6; cursor: default; }
.rdu-note .rdu-x.rdu-x { width: 28px !important; height: 28px !important; padding: 0 !important; border-radius: 8px !important; display: grid; place-items: center;
  box-shadow: none !important; font: 400 15px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important; }
.rdu-note .rdu-go:focus-visible, .rdu-note .rdu-x:focus-visible { outline: 2px solid #5CE1E6 !important; outline-offset: 2px; }
`;

const defaultSecretFor = (id) => chatGameApi.getPlayerSecret(id);

// 📎 review F7: errors the engine throws (not the API's own words) show the plain line
const ENGINE_ERRORS = [TypeError, ReferenceError, SyntaxError, RangeError];
const reasonOf = (err) => ((err instanceof Error && !ENGINE_ERRORS.some((E) => err instanceof E)
  && typeof err.message === 'string' && err.message.trim()) ? err.message.trim() : null);

/** Send each file through the CURRENT upload (read per file: the seat can go mid-run),
 *  one at a time. → the misses: [{ file, msg }] (msg null = the plain line) */
async function sendAll(getUpload, files) {
  const misses = [];
  for (const file of files) {
    const upload = getUpload();
    if (typeof upload !== 'function') { misses.push({ file, msg: null }); continue; }
    try {
      await upload(file, { knowledge: true });
    } catch (err) {
      misses.push({ file, msg: reasonOf(err) });
    }
  }
  return misses;
}

export default function RoomDocsUpload({ gameId, seat, upload, secretFor = defaultSecretFor }) {
  const [failed, setFailed] = useState([]); // [{ file, msg }]
  const [busy, setBusy] = useState(false);
  const alive = useRef(false);
  const uploadRef = useRef(upload); uploadRef.current = upload;
  const secretRef = useRef(secretFor); secretRef.current = secretFor;
  const canUpload = typeof upload === 'function';

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  // the seat (with its secret) is the upload's proof: wait for it, then take
  // this room's files ONCE (take empties the slot, so a re-render or a second
  // mount finds nothing)
  useEffect(() => {
    if (!gameId || !seat || !canUpload) return;
    let secret = null;
    try { secret = secretRef.current ? secretRef.current(seat) : null; } catch (e) { secret = null; }
    if (!secret) return;
    const files = take(gameId);
    if (!files.length) return;
    (async () => {
      const misses = await sendAll(() => uploadRef.current, files);
      if (alive.current && misses.length) setFailed((prev) => [...prev, ...misses]);
    })();
  }, [gameId, seat, canUpload]);

  if (!failed.length) return null;

  const retry = async () => {
    if (busy || typeof uploadRef.current !== 'function') return;
    const files = failed.map((f) => f.file);
    setBusy(true);
    const misses = await sendAll(() => uploadRef.current, files);
    if (!alive.current) return;
    setBusy(false);
    setFailed(misses);
  };

  return (
    <div className="rdu-note" role="status" data-room-docs-note="">
      <style>{CSS}</style>
      <ul className="rdu-list">
        {failed.map(({ file, msg }, i) => (
          <li key={`${file && file.name}|${i}`} data-room-docs-fail={i}>
            <b>{(file && file.name) || 'A doc'}</b>: {msg || ROOM_DOCS_FAILED}
          </li>
        ))}
      </ul>
      <button type="button" className="rdu-go" data-prop-ui="" style={{ '--prop-bg': 'transparent' }} onClick={retry} disabled={busy} data-room-docs-retry="">
        {busy ? 'Adding…' : 'Try again'}
      </button>
      <button type="button" className="rdu-x" data-prop-ui="" style={{ '--prop-bg': 'transparent' }} aria-label="Dismiss" onClick={() => setFailed([])} data-room-docs-dismiss="">✕</button>
    </div>
  );
}
